import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit, Plus, Save, Trash2, Package, Image, Upload, X, Printer } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminPricing() {
  const [products, setProducts] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", category: "textil", base_price: "", description: "", image_url: "", applicable_techniques: [] });
  const [showNewProduct, setShowNewProduct] = useState(false);

  const [editingTechnique, setEditingTechnique] = useState(null);
  const [techForm, setTechForm] = useState({ name: "", description: "", min_quantity: 1 });
  const [showNewTech, setShowNewTech] = useState(false);

  const fetchAll = () => {
    Promise.all([API.get("/products"), API.get("/techniques"), API.get("/gallery")])
      .then(([p, t, g]) => { setProducts(p.data); setTechniques(t.data); setGallery(g.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const getProductImage = (p) => p.image_file_id ? `${API_BASE}/api/public/files/${p.image_file_id}` : null;

  const handleSaveProduct = async () => {
    const payload = { name: productForm.name, category: productForm.category, base_price: parseFloat(productForm.base_price) || 0, description: productForm.description, image_url: productForm.image_url, applicable_techniques: productForm.applicable_techniques };
    if (editingProduct && editingProduct.id) {
      await API.put(`/admin/products/${editingProduct.id}`, payload);
    } else {
      await API.post("/admin/products", payload);
    }
    setEditingProduct(null);
    setShowNewProduct(false);
    fetchAll();
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await API.delete(`/admin/products/${productId}`);
    fetchAll();
  };

  const uploadProductImage = async (productId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await API.post(`/admin/products/${productId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagen subida");
      fetchAll();
    } catch { toast.error("Error al subir imagen"); }
  };

  const handleSaveTechnique = async () => {
    const payload = { name: techForm.name, description: techForm.description, min_quantity: parseInt(techForm.min_quantity) || 1 };
    if (editingTechnique && editingTechnique.id) {
      await API.put(`/admin/techniques/${editingTechnique.id}`, payload);
    } else {
      await API.post("/admin/techniques", payload);
    }
    setEditingTechnique(null);
    setShowNewTech(false);
    fetchAll();
  };

  const handleDeleteTechnique = async (id) => {
    if (!window.confirm("¿Eliminar esta técnica?")) return;
    await API.delete(`/admin/techniques/${id}`);
    fetchAll();
  };

  const uploadGalleryImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await API.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagen agregada");
      fetchAll();
    } catch { toast.error("Error al subir"); }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    await API.delete(`/admin/gallery/${id}`);
    fetchAll();
  };

  const openProductEdit = (product) => {
    setEditingProduct(product);
    setShowNewProduct(false);
    setProductForm({ name: product.name, category: product.category, base_price: String(product.base_price), description: product.description || "", image_url: product.image_url || "", applicable_techniques: product.applicable_techniques || [] });
  };

  const openNewProduct = () => {
    setShowNewProduct(true);
    setEditingProduct({});
    setProductForm({ name: "", category: "textil", base_price: "", description: "", image_url: "", applicable_techniques: techniques.map(t => t.id) });
  };

  const toggleTechnique = (techId) => {
    setProductForm((f) => ({
      ...f,
      applicable_techniques: f.applicable_techniques.includes(techId)
        ? f.applicable_techniques.filter((id) => id !== techId)
        : [...f.applicable_techniques, techId]
    }));
  };

  const getTechNames = (ids) => {
    if (!ids || ids.length === 0) return "Todas";
    return techniques.filter(t => ids.includes(t.id)).map(t => t.name).join(", ");
  };

  return (
    <div className="p-6" data-testid="admin-pricing-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Configuración</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Productos y Precios</h1>
      </div>

      <Tabs defaultValue="products" className="space-y-4">
        <TabsList className="bg-[#F1F5F9] rounded-sm p-1">
          <TabsTrigger value="products" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:text-[#0047AB]" data-testid="tab-products">
            <Package className="w-4 h-4 mr-1" /> Productos
          </TabsTrigger>
          <TabsTrigger value="techniques" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:text-[#0047AB]" data-testid="tab-techniques">
            <Printer className="w-4 h-4 mr-1" /> Técnicas
          </TabsTrigger>
          <TabsTrigger value="gallery" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:text-[#0047AB]" data-testid="tab-gallery">
            <Image className="w-4 h-4 mr-1" /> Galería
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#475569]">Precio incluye IVA e ISR. Descuento 5% desde 300 unidades.</p>
            <Button onClick={openNewProduct} className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C] text-sm" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Producto
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["textil", "promocionales"].map((cat) => (
              <div key={cat} className="bg-white border border-[#E2E8F0] rounded-sm">
                <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <h3 className="text-sm font-bold text-[#0F172A] capitalize" style={{ fontFamily: 'Chivo' }}>{cat}</h3>
                </div>
                <div className="divide-y divide-[#E2E8F0]">
                  {products.filter((p) => p.category === cat).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`prod-row-${p.id}`}>
                      <div className="w-12 h-12 bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
                        {getProductImage(p) ? <img src={getProductImage(p)} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-[#CBD5E1]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A]">{p.name}</p>
                        <p className="text-xs text-[#475569]">Q{p.base_price}</p>
                        <p className="text-[10px] text-[#94A3B8] truncate">{getTechNames(p.applicable_techniques)}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <label className="cursor-pointer p-1.5 rounded-sm hover:bg-[#F1F5F9]" title="Subir imagen">
                          <Image className="w-4 h-4 text-[#0047AB]" />
                          <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadProductImage(p.id, e.target.files[0])} />
                        </label>
                        <Button variant="ghost" size="sm" onClick={() => openProductEdit(p)} className="rounded-sm p-1.5"><Edit className="w-4 h-4 text-[#475569]" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(p.id)} className="rounded-sm p-1.5"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Techniques Tab */}
        <TabsContent value="techniques">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#475569]">Técnicas de impresión disponibles</p>
            <Button onClick={() => { setShowNewTech(true); setEditingTechnique({}); setTechForm({ name: "", description: "", min_quantity: 1 }); }} className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C] text-sm" data-testid="add-technique-btn">
              <Plus className="w-4 h-4 mr-1" /> Nueva Técnica
            </Button>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-sm divide-y divide-[#E2E8F0]">
            {techniques.map((t) => (
              <div key={t.id} className="px-5 py-4 flex items-center justify-between" data-testid={`tech-row-${t.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#F0F4FF] rounded-sm flex items-center justify-center"><Printer className="w-4 h-4 text-[#0047AB]" /></div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{t.name}</p>
                    <p className="text-xs text-[#475569]">{t.description} · Mín. {t.min_quantity} pza(s)</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingTechnique(t); setShowNewTech(false); setTechForm({ name: t.name, description: t.description || "", min_quantity: t.min_quantity || 1 }); }} className="rounded-sm"><Edit className="w-4 h-4 text-[#475569]" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTechnique(t.id)} className="rounded-sm"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Gallery Tab */}
        <TabsContent value="gallery">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#475569]">Imágenes de trabajos realizados</p>
            <label className="inline-flex items-center gap-2 bg-[#0047AB] text-white font-bold rounded-sm px-4 py-2 text-sm hover:bg-[#003A8C] cursor-pointer" data-testid="upload-gallery-btn">
              <Upload className="w-4 h-4" /> Subir Imagen
              <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => { Array.from(e.target.files || []).forEach(uploadGalleryImage); e.target.value = ""; }} />
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.length === 0 ? (
              <div className="col-span-full bg-white border border-dashed border-[#E2E8F0] rounded-sm p-12 text-center">
                <Image className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
                <p className="text-sm text-[#94A3B8]">Sube tu primera imagen de trabajos realizados.</p>
              </div>
            ) : gallery.map((g) => (
              <div key={g.id} className="relative group bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
                <div className="aspect-square"><img src={`${API_BASE}/api/public/files/${g.file_id}`} alt={g.title} className="w-full h-full object-cover" /></div>
                <div className="p-2"><p className="text-xs text-[#475569] truncate">{g.title}</p></div>
                <button onClick={() => deleteGalleryItem(g.id)} className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={!!editingProduct} onOpenChange={() => { setEditingProduct(null); setShowNewProduct(false); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo' }}>{showNewProduct ? "Nuevo Producto" : "Editar Producto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editingProduct?.id && editingProduct?.image_file_id && (
              <div className="w-full h-32 bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm overflow-hidden">
                <img src={`${API_BASE}/api/public/files/${editingProduct.image_file_id}`} alt="" className="w-full h-full object-contain" />
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Nombre *</Label>
              <Input value={productForm.name} onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Playera" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="prod-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Categoría *</Label>
              <Select value={productForm.category} onValueChange={(v) => setProductForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1 border-[#CBD5E1] rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="textil">Textil</SelectItem>
                  <SelectItem value="promocionales">Promocionales</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Precio unitario (Q) *</Label>
              <Input type="number" step="0.01" value={productForm.base_price} onChange={(e) => setProductForm((f) => ({ ...f, base_price: e.target.value }))} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="prod-price-input" />
              <p className="text-[10px] text-[#94A3B8] mt-1">Este precio ya incluye IVA e ISR</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Descripción</Label>
              <Textarea value={productForm.description} onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))} className="mt-1 border-[#CBD5E1] rounded-sm" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Técnicas aplicables</Label>
              <p className="text-[10px] text-[#94A3B8] mb-2">Selecciona las técnicas que aplican para este producto</p>
              <div className="space-y-2">
                {techniques.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 cursor-pointer" data-testid={`tech-checkbox-${t.id}`}>
                    <Checkbox checked={productForm.applicable_techniques.includes(t.id)} onCheckedChange={() => toggleTechnique(t.id)} />
                    <span className="text-sm text-[#0F172A]">{t.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={handleSaveProduct} disabled={!productForm.name || !productForm.base_price} className="w-full bg-[#E30613] text-white font-bold rounded-sm hover:bg-[#C80410]" data-testid="save-product-btn">
              <Save className="w-4 h-4 mr-1" /> {showNewProduct ? "Crear Producto" : "Guardar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Technique Dialog */}
      <Dialog open={!!editingTechnique} onOpenChange={() => { setEditingTechnique(null); setShowNewTech(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo' }}>{showNewTech ? "Nueva Técnica" : "Editar Técnica"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Nombre *</Label>
              <Input value={techForm.name} onChange={(e) => setTechForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Bordado" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="tech-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Descripción</Label>
              <Textarea value={techForm.description} onChange={(e) => setTechForm((f) => ({ ...f, description: e.target.value }))} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="tech-desc-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Cantidad mínima</Label>
              <Input type="number" min="1" value={techForm.min_quantity} onChange={(e) => setTechForm((f) => ({ ...f, min_quantity: e.target.value }))} className="mt-1 border-[#CBD5E1] rounded-sm w-24" data-testid="tech-min-qty-input" />
            </div>
            <Button onClick={handleSaveTechnique} disabled={!techForm.name} className="w-full bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C]" data-testid="save-technique-btn">
              <Save className="w-4 h-4 mr-1" /> {showNewTech ? "Crear Técnica" : "Guardar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
